import { createElement, forwardRef } from 'react';
import { useDesignTokens, renderInlineCssVars } from '../../../theme';
import { useTruncationTooltip } from '../../../hooks/useTruncationTooltip';
import { setRef } from '../../../util/ref-util';
import { SingleBreadcrumbDesignProps } from '.';

// Import component CSS last
import cx from 'classnames';
import cn from './SingleBreadcrumbDesign.css.json';
import './SingleBreadcrumbDesign.css';

export const SingleBreadcrumbDesign = forwardRef<HTMLAnchorElement, SingleBreadcrumbDesignProps>(
  function SingleBreadcrumbDesign(props, ref) {
    const dt = useDesignTokens();
    const tokenOverrides = props.tokens?.();

    let textTokens = {
      fg: dt.enableNovaTokens ? dt.stateStandardNova.normal.fg : dt.stateStandard.normal.fg,
      focus: 'var(--focus-light-bg)',
      'font-family': 'inherit',
    };

    if (dt.themeVariant === 'light' && props.variant === 'primary-light') {
      textTokens = {
        ...textTokens,
        fg: dt.c.colorTextInverse,
        focus: 'var(--focus-dark-bg)',
      };
    }
    if (props.variant === 'primary') {
      textTokens = {
        ...textTokens,
        fg: dt.c.colorTextPrimary,
      };
    }
    if (tokenOverrides?.fg) {
      textTokens.fg = tokenOverrides.fg;
    }
    if (tokenOverrides?.fontFamily) {
      textTokens['font-family'] = tokenOverrides.fontFamily;
    }

    const style = renderInlineCssVars(
      {
        ...textTokens,
        'font-size-m': dt.enableNovaTokens ? dt.fontNova.size.m : dt.font.size.m,
        'font-weight-thick': dt.enableNovaTokens ? dt.fontNova.weight.thick : dt.font.weight.thick,
        'anim-dur-enter': dt.animation.duration.enter.s,
        'anim-tf-enter': dt.animation.timingFunction.enter.m,
      },
      'breadcrumb-text'
    );

    const refTruncation = useTruncationTooltip();

    const setLinkRef = (element: HTMLAnchorElement) => {
      setRef(ref, element);
      setRef(refTruncation, element);
    };

    return (
      <div className={cn.singleItem}>
        <a
          ref={setLinkRef}
          href="#"
          className={cx(
            cn.itemText,
            props.animated && cn.animated,
            props.isCurrent ? cn.current : cn.interactive,
            cn.opening
          )}
          style={{ ...style, ...props.style }}
          tabIndex={props.focused ? 0 : -1}
          aria-current={props.isCurrent ? 'page' : undefined}
          onClick={(e) => {
            if (props.onPress) {
              e.preventDefault();
              props.onPress(props.index);
            }
          }}
          onKeyDown={(event) => props.onKeyDown?.(event)}
          onFocus={props.onFocus}
          data-testid={props.testId}
        >
          {props.value}
        </a>
      </div>
    );
  }
);
